import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Rocket, BookOpen, Timer, Sparkles, Loader2, TrendingDown, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { API, useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';

export default function NavodayaPracticePage() {
  const { user, login } = useAuth();
  const [classNum, setClassNum] = useState(6);
  const [syllabus, setSyllabus] = useState(null);
  const [section, setSection] = useState(null);
  const [topic, setTopic] = useState(null);
  const [difficulty, setDifficulty] = useState('medium');
  const [generating, setGenerating] = useState(false);
  const [pack, setPack] = useState(null); // generated question pack
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [startTime, setStartTime] = useState(null);

  useEffect(() => {
    setSection(null); setTopic(null); setPack(null); setResult(null);
    axios.get(`${API}/navodaya/syllabus/${classNum}`).then(r => setSyllabus(r.data)).catch(() => setSyllabus(null));
  }, [classNum]);

  useEffect(() => {
    if (user) axios.get(`${API}/navodaya/performance`, { withCredentials: true }).then(r => setPerformance(r.data)).catch(() => {});
  }, [user, result]);

  const generate = async () => {
    if (!user) { login(); return; }
    if (!section || !topic) { toast({ title: 'Section ଓ Topic ଚୟନ କରନ୍ତୁ' }); return; }
    setGenerating(true);
    setPack(null); setResult(null);
    try {
      const res = await axios.post(`${API}/navodaya/practice/generate`, {
        class_num: classNum, section, topic, difficulty, num_questions: 5
      }, { withCredentials: true });
      setPack(res.data);
      setAnswers(new Array(res.data.questions.length).fill(-1));
      setStartTime(Date.now());
      toast({ title: `${res.data.questions.length} AI questions ପ୍ରସ୍ତୁତ ✅` });
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || 'Try again' });
    } finally { setGenerating(false); }
  };

  const submit = async () => {
    if (!pack) return;
    const timeTaken = startTime ? Math.round((Date.now() - startTime) / 1000) : 0;
    try {
      const res = await axios.post(`${API}/navodaya/practice/submit`, {
        practice_id: pack.id, answers, time_taken_sec: timeTaken
      }, { withCredentials: true });
      setResult(res.data);
    } catch (e) {
      toast({ title: 'Submit failed', description: e?.response?.data?.detail || 'Try again' });
    }
  };

  return (
    <div className="px-4 pt-4 space-y-4 pb-8">
      <Link to="/navodaya" className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-emerald-600 font-medium" data-testid="nav-practice-back">
        <ArrowLeft size={16} /> Navodaya Home
      </Link>

      <div className="rounded-3xl p-5 text-white shadow-lg" style={{ background: 'linear-gradient(135deg, #f97316 0%, #dc2626 60%, #7c3aed 100%)' }}>
        <Rocket size={26} />
        <h1 className="text-xl font-extrabold mt-2">🎯 Navodaya AI Practice</h1>
        <p className="text-xs text-white/90 mt-1">JNVST Class 6 & 9 · AI-Generated Practice Questions</p>
      </div>

      <Card className="rounded-2xl p-3 border-amber-200 bg-amber-50">
        <div className="flex items-start gap-2">
          <AlertCircle size={14} className="text-amber-600 shrink-0 mt-0.5" />
          <p className="text-[10px] text-amber-800 leading-relaxed">
            <strong>Note:</strong> Questions are <strong>AI GENERATED PRACTICE</strong> — they are NOT official NVS/JNVST questions. Use these for practice; refer to <a href="https://navodaya.gov.in" target="_blank" rel="noreferrer" className="underline">navodaya.gov.in</a> for official material.
          </p>
        </div>
      </Card>

      {/* Class selector */}
      <div className="flex gap-2">
        {[6, 9].map(c => (
          <button key={c} onClick={() => setClassNum(c)} data-testid={`nav-class-${c}`}
            className={`flex-1 py-2 rounded-xl text-sm font-bold ${classNum === c ? 'bg-orange-600 text-white' : 'bg-white border-2 border-gray-200 text-gray-700'}`}>
            JNVST Class {c}
          </button>
        ))}
      </div>

      {/* Exam pattern & section picker */}
      {syllabus && !pack && (
        <Card className="rounded-2xl p-4 border-gray-200">
          <div className="font-bold text-sm text-gray-900 mb-2">📖 {syllabus.exam} · Class {syllabus.class}</div>
          <div className="text-[11px] text-gray-500 mb-3">
            {syllabus.exam_pattern.total_questions} questions · {syllabus.exam_pattern.duration_minutes} min · {syllabus.exam_pattern.sections.length} sections
          </div>
          <div className="space-y-2">
            {syllabus.exam_pattern.sections.map(s => (
              <button key={s.name} onClick={() => { setSection(s.name); setTopic(null); }} data-testid={`nav-section-${s.name.replace(/\s+/g,'-')}`}
                className={`w-full text-left p-3 rounded-xl border-2 transition-all ${section === s.name ? 'border-orange-500 bg-orange-50' : 'border-gray-200 bg-white hover:border-orange-300'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-bold text-sm text-gray-900">{s.name}</div>
                    <div className="text-[11px] text-gray-500">{s.questions} Q · {s.marks} marks{s.time_minutes ? ` · ${s.time_minutes} min` : ''}</div>
                  </div>
                  <BookOpen size={16} className="text-orange-500" />
                </div>
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* Topic picker */}
      {section && syllabus?.topics?.[section] && !pack && (
        <Card className="rounded-2xl p-4 border-gray-200">
          <div className="font-bold text-sm text-gray-900 mb-2">📚 Topic · {section}</div>
          <div className="grid grid-cols-2 gap-2">
            {syllabus.topics[section].map(t => (
              <button key={t} onClick={() => setTopic(t)} data-testid={`nav-topic-${t.replace(/\s+/g,'-')}`}
                className={`p-2 rounded-xl text-[11px] font-semibold text-left ${topic === t ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-800 hover:bg-orange-100'}`}>
                {t}
              </button>
            ))}
          </div>
          {topic && (
            <>
              <div className="mt-3 flex gap-2">
                {['easy','medium','hard'].map(d => (
                  <button key={d} onClick={() => setDifficulty(d)}
                    className={`flex-1 py-1.5 rounded-lg text-xs font-bold ${difficulty === d ? 'bg-orange-600 text-white' : 'bg-white border border-gray-300 text-gray-700'}`}>
                    {d}
                  </button>
                ))}
              </div>
              <Button onClick={generate} disabled={generating} data-testid="nav-generate-btn"
                className="mt-3 w-full h-11 rounded-xl bg-orange-600 hover:bg-orange-700 text-white font-extrabold">
                {generating ? <><Loader2 className="animate-spin mr-2" size={16}/> Generating...</> : <><Sparkles size={16} className="mr-1"/> Generate 5 AI Questions</>}
              </Button>
            </>
          )}
        </Card>
      )}

      {/* Practice questions */}
      {pack && !result && (
        <Card className="rounded-2xl p-4 border-orange-200 bg-orange-50">
          <div className="flex items-center justify-between mb-3">
            <div className="font-extrabold text-orange-900">🎯 Practice: {pack.topic}</div>
            <span className="text-[9px] px-2 py-0.5 rounded-full bg-orange-600 text-white font-bold">AI GENERATED</span>
          </div>
          <div className="space-y-4">
            {pack.questions.map((q, i) => (
              <div key={i} className="bg-white rounded-xl p-3 border border-orange-200" data-testid={`nav-q-${i}`}>
                <div className="font-bold text-sm text-gray-900 mb-2">{i + 1}. {q.q}</div>
                <div className="space-y-1.5">
                  {q.options.map((opt, oi) => (
                    <button key={oi} onClick={() => setAnswers(a => { const n = [...a]; n[i] = oi; return n; })}
                      data-testid={`nav-q-${i}-opt-${oi}`}
                      className={`w-full text-left p-2 rounded-lg text-xs border-2 transition-all ${answers[i] === oi ? 'border-orange-500 bg-orange-100 font-bold' : 'border-gray-200 bg-gray-50 hover:border-orange-300'}`}>
                      <span className="font-bold mr-2">{String.fromCharCode(65 + oi)}.</span> {opt}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <Button onClick={submit} data-testid="nav-submit-btn"
            className="w-full mt-4 h-11 rounded-xl bg-orange-600 hover:bg-orange-700 text-white font-extrabold">
            Submit Practice
          </Button>
        </Card>
      )}

      {/* Result */}
      {result && (
        <Card className="rounded-2xl p-4 border-emerald-200 bg-emerald-50" data-testid="nav-result">
          <div className="text-center mb-3">
            <div className="text-4xl font-extrabold text-emerald-700">{result.score}/{result.total}</div>
            <div className="text-sm font-bold text-emerald-800">{result.percentage}% · {Math.round(result.time_taken_sec / 60)}m {result.time_taken_sec % 60}s</div>
          </div>
          <div className="space-y-2">
            {result.detail.map((d, i) => (
              <div key={i} className={`p-2 rounded-lg text-xs flex items-start gap-2 ${d.is_right ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`}>
                {d.is_right ? <CheckCircle2 size={14} className="shrink-0 mt-0.5"/> : <XCircle size={14} className="shrink-0 mt-0.5"/>}
                <div className="flex-1 min-w-0">
                  <div className="font-bold">Q{i + 1}: {pack.questions[i].q.slice(0, 80)}{pack.questions[i].q.length > 80 ? '...' : ''}</div>
                  {!d.is_right && <div className="text-[10px] mt-1 opacity-90">Correct: {String.fromCharCode(65 + d.correct_index)} — {pack.questions[i].explanation?.slice(0, 200)}</div>}
                </div>
              </div>
            ))}
          </div>
          <Button onClick={() => { setPack(null); setResult(null); }}
            className="w-full mt-3 h-10 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold">
            Try Another
          </Button>
        </Card>
      )}

      {/* Performance */}
      {performance && performance.total_attempts > 0 && !pack && (
        <Card className="rounded-2xl p-4 border-purple-200">
          <div className="font-bold text-sm text-gray-900 mb-2">📊 Your Performance</div>
          <div className="grid grid-cols-2 gap-2 text-center">
            <div className="p-2 rounded-lg bg-purple-50">
              <div className="text-2xl font-extrabold text-purple-700">{performance.total_attempts}</div>
              <div className="text-[10px] text-purple-600">Attempts</div>
            </div>
            <div className="p-2 rounded-lg bg-purple-50">
              <div className="text-2xl font-extrabold text-purple-700">{performance.avg_percentage}%</div>
              <div className="text-[10px] text-purple-600">Avg</div>
            </div>
          </div>
          {performance.weakest_topics?.length > 0 && (
            <div className="mt-3">
              <div className="text-xs font-bold text-rose-700 flex items-center gap-1 mb-1"><TrendingDown size={12}/> Weakest topics</div>
              {performance.weakest_topics.map(w => (
                <div key={w.topic} className="text-[11px] py-1 flex items-center justify-between">
                  <span className="text-gray-700 truncate flex-1">{w.topic}</span>
                  <span className="font-bold text-rose-600">{w.avg_percentage}%</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
